import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Dropdown, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppContext } from 'providers/AppProvider';
import { useUserContext } from 'providers/UserProvider';

import FalconCloseButton from 'components/common/FalconCloseButton';

import { searchApi } from 'api';
import paths from 'routes/paths';

// SearchTypes moved inside component to access tenant and warehouse

const SearchInput = () => {
  const {
    config: { isDark },
  } = useAppContext();

  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const navigate = useNavigate();

  // SearchTypes defined inside component to access tenant and warehouse
  const SearchTypes = useMemo(
    () => [
      {
        label: 'Order',
        queryParam: 'channel_order_id',
        endpoint: 'orders',
        getUrl: id =>
          `${paths.orders.details(tenant, warehouse, id)}?tab=order-items`,
      },
      {
        label: 'Shipment',
        queryParam: 'shipment',
        endpoint: 'orders',
        getUrl: id =>
          `${paths.orders.details(tenant, warehouse, id)}?tab=shipments`,
      },
      {
        label: 'Invoice',
        queryParam: 'invoice_code',
        endpoint: 'orders',
        getUrl: id =>
          `${paths.orders.details(tenant, warehouse, id)}?tab=invoices`,
      },
      {
        label: 'Product',
        queryParam: 'product',
        endpoint: 'products',
        getUrl: id => `${paths.products.details(tenant, warehouse, id)}`,
      },
      {
        label: 'Manifest',
        queryParam: 'manifest_code',
        endpoint: 'manifests',
        getUrl: id => `${paths.manifests.details(tenant, warehouse, id)}`,
      },
    ],
    [tenant, warehouse],
  );

  const [searchTypeLabel, setSearchTypeLabel] = useState(SearchTypes[0].label);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchState, setSearchState] = useState({
    isLoading: false,
    isLoadingMore: false,
    results: [],
    totalCount: 0,
    page: 1,
    hasNext: false,
    showDropdown: false,
  });

  const emptySearchState = showDropdown => ({
    isLoading: false,
    isLoadingMore: false,
    results: [],
    totalCount: 0,
    page: 1,
    hasNext: false,
    showDropdown,
  });

  // 현재 선택된 SearchType 객체 가져오기
  const currentSearchType = useMemo(
    () => SearchTypes.find(t => t.label === searchTypeLabel),
    [SearchTypes, searchTypeLabel],
  );

  const handleNavigateToDetail = id => {
    setSearchTerm('');
    setSearchState(emptySearchState(false));
    navigate(currentSearchType.getUrl(id));
  };

  const fetchSearchPage = async page => {
    const params = new URLSearchParams();
    params.append(currentSearchType.queryParam, searchTerm);
    params.append('page', String(page));

    let result;
    if (currentSearchType.endpoint === 'orders') {
      result = await searchApi.getSearchResultOfOrders(params);
    } else if (currentSearchType.endpoint === 'products') {
      result = await searchApi.getSearchResultOfProducts(params);
    } else if (currentSearchType.endpoint === 'manifests') {
      result = await searchApi.getSearchResultOfManifests(params);
    }

    // Paginated response: {count, next, previous, results}. Bare array kept
    // for compatibility with a server that is not paginated yet.
    if (Array.isArray(result)) {
      return { results: result, totalCount: result.length, hasNext: false };
    }
    const results = result?.results || [];
    return {
      results,
      totalCount:
        typeof result?.count === 'number' ? result.count : results.length,
      hasNext: Boolean(result?.next),
    };
  };

  const handleSearch = async e => {
    e.preventDefault();

    if (!searchTerm.trim()) {
      setSearchState(prev => ({ ...prev, showDropdown: false }));
      return;
    }
    setSearchState(prev => ({ ...prev, isLoading: true }));

    try {
      const { results, totalCount, hasNext } = await fetchSearchPage(1);

      if (results.length === 1 && totalCount === 1) {
        handleNavigateToDetail(results[0].id);
      } else {
        setSearchState({
          isLoading: false,
          isLoadingMore: false,
          results,
          totalCount,
          page: 1,
          hasNext,
          showDropdown: true,
        });
      }
    } catch (error) {
      setSearchState(emptySearchState(true));
    }
  };

  const handleLoadMore = async e => {
    e.preventDefault();
    e.stopPropagation();

    const nextPage = searchState.page + 1;
    setSearchState(prev => ({ ...prev, isLoadingMore: true }));

    try {
      const { results, totalCount, hasNext } = await fetchSearchPage(nextPage);

      setSearchState(prev => {
        const seen = new Set(prev.results.map(r => r.id));
        return {
          ...prev,
          isLoadingMore: false,
          results: [...prev.results, ...results.filter(r => !seen.has(r.id))],
          totalCount,
          page: nextPage,
          hasNext,
        };
      });
    } catch (error) {
      setSearchState(prev => ({ ...prev, isLoadingMore: false }));
    }
  };

  const handleSearchTypeClick = typeLabel => {
    setSearchTypeLabel(typeLabel);
    setSearchState(emptySearchState(false));
  };

  const handleSearchTermChange = e => {
    setSearchTerm(e.target.value);

    setSearchState(emptySearchState(false));
  };

  const handleReset = () => {
    setSearchTerm('');
    setSearchState(emptySearchState(false));
  };

  return (
    <Dropdown
      show={searchState.showDropdown && searchTerm.trim()}
      className="search-box"
    >
      <Dropdown.Toggle as="div" className="dropdown-caret-none">
        <Form className="position-relative" onSubmit={handleSearch}>
          <div
            className={`d-flex align-items-center ${
              !isDark ? 'bg-white' : ''
            } rounded-pill`}
          >
            <FontAwesomeIcon
              icon="search"
              className="position-absolute text-400 search-box-icon"
              style={{ left: '1rem', zIndex: 1 }}
            />

            <Dropdown as="div" className="ms-4 ps-2">
              <Dropdown.Toggle
                variant="link"
                className="text-decoration-none text-700 bg-transparent border-0 p-0"
                style={{ boxShadow: 'none' }}
              >
                {currentSearchType.label}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ zIndex: 1050 }}>
                {SearchTypes.map(type => (
                  <Dropdown.Item
                    key={type.label}
                    onClick={() => handleSearchTypeClick(type.label)}
                    active={searchTypeLabel === type.label}
                  >
                    {type.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <Form.Control
              type="search"
              placeholder={`Search by ${currentSearchType.label.toLowerCase()}...`}
              className="rounded-pill search-input border-0 ps-2"
              value={searchTerm}
              onChange={handleSearchTermChange}
              readOnly={searchState.isLoading}
              style={{ boxShadow: 'none' }}
            />

            {searchTerm && (
              <div className="search-box-close-btn-container">
                <FalconCloseButton
                  size="sm"
                  noOutline
                  className="fs-11"
                  onClick={handleReset}
                />
              </div>
            )}
          </div>
        </Form>
      </Dropdown.Toggle>

      <Dropdown.Menu className="w-100">
        <div className="scrollbar py-3" style={{ maxHeight: '24rem' }}>
          {searchTerm &&
            (searchState.results.length > 0 ? (
              <>
                {searchState.results.map(result => (
                  <Dropdown.Item
                    key={result.id}
                    onClick={() => handleNavigateToDetail(result.id)}
                    className="px-x1 py-2"
                  >
                    {result.display_name}
                  </Dropdown.Item>
                ))}
                {searchState.totalCount > searchState.results.length && (
                  <div className="px-x1 mt-2 text-center">
                    <p className="fs-10 text-600 mb-2">
                      {`Showing ${searchState.results.length} of ${searchState.totalCount} results.`}
                    </p>
                    {searchState.hasNext && (
                      <Button
                        variant="falcon-default"
                        size="sm"
                        onClick={handleLoadMore}
                        disabled={searchState.isLoadingMore}
                      >
                        {searchState.isLoadingMore ? 'Loading...' : 'Load more'}
                      </Button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="px-x1">
                <p className="fs-8 fw-bold text-center mb-0">
                  No results found
                </p>
              </div>
            ))}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default SearchInput;
